import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Movie } from '../../types';
import { useAuth } from '../../context/AuthContext';
import './MovieCard.css';

interface MovieCardProps {
  movie: Movie;
  onClickOverride?: (movie: Movie) => void;
  showLogo?: boolean;
  featured?: boolean;
}

const MovieCard: React.FC<MovieCardProps> = ({ movie, onClickOverride, showLogo = true, featured = false }) => {
  const { isAuthenticated, subscription, user } = useAuth();
  const navigate = useNavigate();

  const getThumbnailUrl = (thumbnail: string) => {
    if (thumbnail.startsWith('http://') || thumbnail.startsWith('https://')) {
      return thumbnail;
    }
    return `/public/uploads/thumbnails/${thumbnail}`;
  };

  const handleClick = (e: React.MouseEvent) => {
    if (onClickOverride) {
      e.preventDefault();
      onClickOverride(movie);
      return;
    }

    // Check authentication
    if (!isAuthenticated) {
      e.preventDefault();
      navigate(`/login?redirect=/movies/${movie.id}`);
      return;
    }

    // Admin users have full access
    if (user?.role === 'admin') {
      return; // Allow navigation
    }

    // Check subscription for regular users
    if (!subscription?.has_subscription) {
      e.preventDefault();
      // Show subscription modal or redirect
      navigate('/subscribe');
      return;
    }
  };

  return (
    <Link 
      to={`/movies/${movie.id}`}
      onClick={handleClick}
      className={`movie-card group relative overflow-hidden rounded-lg cursor-pointer ${featured ? 'featured-movie-card' : ''}`}
    >
      {/* CineClubs Logo */}
      {showLogo && (
        <img
          src="/public/assets/images/logoformovies.png"
          alt="CineClubs"
          className={`movie-thumbnail-logo absolute z-10 opacity-90 group-hover:opacity-100 transition-opacity ${featured ? 'top-1 left-1 w-7 h-7' : 'top-2 left-2 w-11 h-11'}`}
          loading="lazy"
        />
      )}

      {/* Movie Thumbnail */}
      <div className="aspect-[2/3] overflow-hidden">
        <img
          src={getThumbnailUrl(movie.thumbnail)}
          alt={`${movie.title} movie poster`}
          className="w-full h-full object-cover transition-all duration-300 group-hover:brightness-90"
          loading="lazy"
          onError={(e) => {
            (e.target as HTMLImageElement).src = '/public/assets/images/no-image.jpg';
          }}
        />
      </div>

      {/* Hover Overlay */}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/70 transition-all duration-300 flex items-center justify-center">
        <i className="fas fa-play-circle text-white text-3xl lg:text-4xl opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-50 group-hover:scale-100"></i>
      </div>

      {/* Movie Info on Hover */}
      <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/90 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300">
        <p className="text-white text-xs font-bold truncate mb-1">{movie.title}</p>
        <div className="flex items-center justify-center gap-1">
          <span className="inline-flex items-center bg-yellow-400 text-black px-1.5 py-0.5 rounded text-[10px] font-bold">
            <span className="font-black mr-0.5">IMDb</span>
            <i className="fas fa-star text-black text-[8px] mr-0.5"></i>
            {movie.rating || 'N/A'}
          </span>
        </div>
      </div>

      {/* Movie Info Card (for non-featured) */}
      {!featured && (
        <div className="movie-info p-3 bg-gradient-to-t from-[#141414] to-[#1a1a1a]">
          <h3 className="movie-title text-sm font-semibold text-white truncate mb-1">{movie.title}</h3>
          <div className="movie-meta flex items-center justify-between text-xs text-gray-400">
            {movie.genre && (
              <span className="movie-genre px-2 py-0.5 bg-white/10 rounded text-xs truncate max-w-[50%]">
                {movie.genre}
              </span>
            )}
            <span className="inline-flex items-center bg-yellow-400 text-black px-2 py-0.5 rounded text-xs font-bold">
              <span className="font-black mr-1">IMDb</span>
              <i className="fas fa-star text-black text-xs mr-0.5"></i>
              {movie.rating || 'N/A'}
            </span>
          </div>
        </div>
      )}
    </Link>
  );
};

export default MovieCard;
